class Collection {
  constructor() {
    this.selectors = {
      section: ".facest-filters-section",
      toolbar: ".m-collection-toolbar",
      cols: [".m-toolbar--column-switcher button"],
      productContainer: "[data-product-container]",
      loadMoreBtn: "[data-load-more]",
      sidebar: ".m-sidebar",
      sidebarContent: ".m-sidebar--content",
      openSidebar: ".m-sidebar--open",
      closeSidebar: ".m-sidebar--close",
      filtersWrapper: ".m-filter--wrapper",
    };

    this.mobileSortingSelectors = {
      openSorting: ".m-sortby--open",
      closeSorting: ".m-sortby-mobile--close",
      sortingWrapper: ".m-sortby-mobile--wrapper",
      sortingList: ".m-sortby-mobile--content",
    };

    this.sectionId = "";
    this.isDesignMode = window.MinimogSettings?.design_mode;
    this.currentPage = 1;
    this.paginationType = undefined;
    this.totalPages = 0;
    this.totalProducts = 0;
    this.infiniteLoadingObserver = undefined;
    this.showColSwitchers = false;
    this.cachedCol = undefined;
    this.initialCol = 1;
    this.activeCol = null;
    this.STORAGE_KEY = "gridColumnViews";
    this.MAX_COL_BY_SCREEN_SIZE = {
      mobile: 2,
      tablet: 4,
      desktop: 5,
    };
    this.enableFilters = false;
    this.enableSorting = false;
    this.filtersType = undefined;
    this.filtersPosition = undefined;
    this.accordions = undefined;
    this.sideEffectEventsAdded = false;
  }

  init = () => {
    this.domNodes = queryDomNodes(this.selectors);
    this.mbSortingNodes = queryDomNodes(this.mobileSortingSelectors);

    this.setData();
    this.initGridView();
    this.initFilters();
    this.initMobileSorting();
    this.initProducts();

    this.sideEffectEventsAdded = true;

    MinimogEvents.subscribe("ON_PRODUCT_LIST_UPDATED", () =>
      refreshProductReview()
    );

    this.handleStickyToolbar();
    document.addEventListener("matchMobile", () => this.handleStickyToolbar());
    document.addEventListener("unmatchMobile", () => this.handleStickyToolbar());
  };

  setData = () => {
    const { section: t, productContainer: e } = this.domNodes;
    if (!t) return;

    this.sectionId = t.dataset.sectionId || "";
    this.paginationType = t.dataset.paginationType || "paginate";
    this.totalPages = e ? Number(e.dataset.totalPages || 1) : 1;
    this.view = t.dataset.view || "";
    this.activeCol = null;
    this.currentPage = 1;
    this.initialCol = Number(t.dataset.initialColumn || 1);
    this.showColSwitchers = t.dataset.showColSwitchers === "true";
    this.cachedCol = this.showColSwitchers
      ? Number(window.localStorage.getItem(this.STORAGE_KEY)) || this.initialCol
      : this.initialCol;
    this.enableFilters = t.dataset.enableFilters === "true";
    this.enableSorting = t.dataset.enableSorting === "true";
    this.filtersType = t.dataset.filtersType || "";
    this.filtersPosition = t.dataset.filtersPosition || "";
  };

  initGridView = () => {
    const { toolbar } = this.domNodes;
    // 1) Decide the starting column
    const device = this.getDeviceByScreenSize();
    const maxCols = this.MAX_COL_BY_SCREEN_SIZE[device] || this.initialCol;

    let startCol = this.showColSwitchers ? (this.cachedCol || this.initialCol) : this.initialCol;
    // Clamp to device limits and minimum 1
    startCol = Math.max(1, Math.min(startCol, maxCols));

    // 2) Apply once
    this.toggleView(startCol);

    // 3) Keep in bounds when screen resizes
    if (!this.sideEffectEventsAdded) {
      window.addEventListener("resize", debounce(this.updateViewByScreen, 500));
    }

    // 4) Column switcher clicks (event delegation)
    if (toolbar) {
      addEventDelegate({
        selector: this.selectors.cols[0],
        context: toolbar,
        handler: (_evt, btn) => {
          const col = Number(btn?.dataset?.column);
          if (!Number.isNaN(col)) this.toggleView(col);
        },
      });
    }

    // 5) Pagination
    this.initLoadMore();

    // 6) Also enforce caps once at init
    this.updateViewByScreen();
  };

  initFilters = () => {
    if (!this.enableFilters) return;
    const { sidebar: t, openSidebar: e, closeSidebar: i } = this.domNodes;
    if (!t) return;

    window.requestAnimationFrame(this.initAccordions);

    e && e.addEventListener("click", this.openSidebarFilter);
    i && i.addEventListener("click", this.closeSidebarFilter);
    t.addEventListener("click", (ev) => {
      if (ev.target === t) this.closeSidebarFilter();
    });
  };

  initMobileSorting = () => {
    if (!this.enableSorting) return;
    const { openSorting: t, closeSorting: e, sortingWrapper: i } =
      this.mbSortingNodes;
    if (!i) return;

    t && t.addEventListener("click", this.openMobileSorting);
    e && e.addEventListener("click", this.closeMobileSorting);
    i.addEventListener("click", (ev) => {
      if (ev.target === i) this.closeMobileSorting();
    });
  };

  openMobileSorting = () => {
    const { sortingWrapper: t, sortingList: e } = this.mbSortingNodes;
    if (!t || !e) return;
    t.style.display = "block";
    window.requestAnimationFrame(() => {
      t.style.setProperty("--m-bg-opacity", "0.5");
      e.style.setProperty("--m-translate-y", "0");
    });
  };

  closeMobileSorting = () => {
    const { sortingWrapper: e, sortingList: i } = this.mbSortingNodes;
    if (!e || !i) return;
    e.style.setProperty("--m-bg-opacity", "0");
    i.style.setProperty("--m-translate-y", "100%");
    setTimeout(() => e.style.setProperty("display", "none"), 300);
  };

  initAccordions = () => {
    this.accordions && this.accordions.destroy();
    const { filtersWrapper: t } = this.domNodes;
    if (!t) return;

    t.classList.remove("acc-initialized");
    this.accordions = new MinimogLibs.Accordion(t, {
      presetContentHeight:
        window.innerWidth > 1280 && this.filtersPosition !== "fixed",
      callback: () => t.style.setProperty("opacity", "1"),
    });
  };

  openSidebarFilter = () => {
    const { sidebar: t, sidebarContent: e, section: i } = this.domNodes;
    if (!t || !e || !i) return;

    t.style.display = "block";
    window.requestAnimationFrame(() => {
      t.style.setProperty("--m-bg-opacity", "0.5");
      e.style.setProperty("--m-translate-x", "0");
      this.accordions && this.accordions.setContentHeight();
    });
    i.classList.add("sidebar-open");
  };

  closeSidebarFilter = () => {
    const { sidebar: t, sidebarContent: e, section: i } = this.domNodes;
    if (!t || !e || !i) return;

    i.classList.remove("sidebar-open");
    if (window.innerWidth < 1280 || t.dataset.type === "fixed") {
      e.style.setProperty("--m-translate-x", "-100%");
      t.style.removeProperty("--m-bg-opacity");
      setTimeout(() => t.style.removeProperty("display"), 300);
    }
  };

  toggleView = (col) => {
    if (!col || this.view === "search") return;

    const { cols: e, productContainer: i } = this.domNodes;
    if (!i) return;

    // Reconnect <responsive-image> to recalc sizes on grid change
    const responsiveImages = i.querySelectorAll("responsive-image");
    if (responsiveImages?.length) {
      responsiveImages.forEach((ri) => {
        if (typeof ri.disconnectedCallback === "function") ri.disconnectedCallback();
        if (typeof ri.connectedCallback === "function") ri.connectedCallback();
      });
    }

    const prev = this.activeCol || this.initialCol || 1;

    if (this.showColSwitchers && e) {
      const buttons = (this.domNodes.toolbar || document).querySelectorAll(
        this.selectors.cols[0]
      );
      buttons[prev - 1]?.classList?.remove("active");
      buttons[col - 1]?.classList?.add("active");
    }

    i.classList.remove(`m-cols-${prev}`);
    i.classList.add(`m-cols-${col}`);

    if (this.showColSwitchers) window.localStorage.setItem(this.STORAGE_KEY, String(col));
    this.activeCol = col;
  };

  updateViewByScreen = () => {
    // If we have no active col yet, don't force to 1 — respect initial/cached
    const current = this.activeCol || this.initialCol || 1;
    const device = this.getDeviceByScreenSize();
    const max = this.MAX_COL_BY_SCREEN_SIZE[device] || current;

    if (current > max) this.toggleView(max);
  };

  getDeviceByScreenSize = () =>
    window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop";

  initLoadMore = () => {
    if (this.paginationType === "paginate" || this.totalPages <= 1 || this.view === "search") return;
    const { loadMoreBtn: t } = this.domNodes;
    if (!t) return;

    t.addEventListener("click", this.loadMoreProducts);

    if (this.paginationType === "infinite" && "IntersectionObserver" in window) {
      this.infiniteLoadingObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio === 1) this.loadMoreProducts();
          });
        },
        { threshold: 1 }
      );
      this.infiniteLoadingObserver.observe(t);
    }
  };

  loadMoreProducts = () => {
    const nextPage = this.currentPage + 1;
    if (nextPage > this.totalPages) return;

    const { productContainer: e, loadMoreBtn: i } = this.domNodes;
    if (!e || !i) return;

    this.toggleLoading(true);

    fetchSection(this.sectionId, {
      fromCache: true,
      params: { page: nextPage },
    })
      .then((doc) => {
        doc
          .querySelectorAll("[data-product-container] .m-product-item")
          .forEach((node) => e.appendChild(node));
        this.initProducts();
      })
      .catch(() => {})
      .finally(() => {
        MinimogEvents.emit("ON_PRODUCT_LIST_UPDATED");
        this.toggleLoading(false);
        this.currentPage = nextPage;

        if (nextPage >= this.totalPages) {
          i.parentNode && i.parentNode.remove();
          this.infiniteLoadingObserver && this.infiniteLoadingObserver.unobserve(i);
        }
      });
  };

  initProducts = () => {
    MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState?.();
    MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState?.();
  };

  toggleLoading = (isLoading) => {
    const { loadMoreBtn: e } = this.domNodes;
    if (!e) return;
    e.classList[isLoading ? "add" : "remove"]("m-spinner-loading");
  };

  handleStickyToolbar() {
    const t = this.domNodes?.toolbar;
    if (!t) return;

    const startTop = t.offsetTop || 0;
    let lastY = 0;

    window.addEventListener("scroll", () => {
      if (!MinimogTheme?.config?.mqlMobile) {
        t.classList.remove("scroll-up", "m-collection-toolbar--sticky", "scroll-down");
        return;
      }

      const y = window.scrollY;
      if (y <= startTop) {
        t.classList.remove("scroll-up", "m-collection-toolbar--sticky");
        return;
      }

      t.classList.add("m-collection-toolbar--sticky");

      if (y > t.offsetHeight + startTop && y > lastY && !t.classList.contains("scroll-down")) {
        t.classList.remove("scroll-up");
        t.classList.add("scroll-down");
      } else if (y < lastY && t.classList.contains("scroll-down")) {
        t.classList.remove("scroll-down");
        t.classList.add("scroll-up");
      }

      lastY = y;
    });
  }
}
